import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  ExternalLink, Copy, Bookmark, Heart, MessageSquare,
  Edit2, RefreshCw, Trash2, X, AlertTriangle, Check, Loader2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { saveArticleLocally } from './LibraryPage';

// Helper to generate URL-friendly slug from title
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable MenuItem component - defined outside to avoid re-creation on each render
const MenuItem = ({ icon: Icon, label, onClick, danger, disabled, loading, active }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors disabled:opacity-50 ${
      danger 
        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20' 
        : active
          ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`}
  >
    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className={`h-4 w-4 ${active ? 'fill-current' : ''}`} />}
    <span className="text-sm font-medium">{label}</span>
  </button>
);

// Context Menu Provider
export const ArticleContextMenuContext = React.createContext(null);

export const useArticleContextMenu = () => {
  const context = React.useContext(ArticleContextMenuContext);
  if (!context) {
    throw new Error('useArticleContextMenu must be used within ArticleContextMenuProvider');
  }
  return context;
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, articleTitle, isAdmin, loading }) => {
  const handleBackdropClick = (e) => {
    e.stopPropagation();
    if (!loading) onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div 
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm p-5 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-full flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isAdmin ? 'Delete Article?' : 'Request Deletion?'}
          </h3>
        </div>
        
        <div className="mb-5">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {isAdmin 
              ? 'This action cannot be undone. The article will be permanently removed.'
              : 'Your deletion request will be sent to an admin for approval.'}
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Article:</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white break-words line-clamp-2">
              {articleTitle || 'Untitled Article'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onConfirm(); }}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {isAdmin ? 'Delete' : 'Request'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Status Change Modal
const StatusChangeModal = ({ isOpen, onClose, onConfirm, articleTitle, currentStatus, isAdmin, loading }) => {
  // Initialize with currentStatus, reset when modal opens
  const [newStatus, setNewStatus] = useState(currentStatus);
  
  // Reset status when modal opens or currentStatus changes - using key pattern instead
  const statusToShow = isOpen ? (newStatus !== currentStatus && newStatus ? newStatus : currentStatus) : currentStatus;
  
  // Reset when opening
  const handleStatusChange = (status) => {
    setNewStatus(status);
  };
  
  const statuses = isAdmin 
    ? ['published', 'pending', 'draft', 'rejected']
    : ['pending', 'draft'];

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { e.stopPropagation(); if (!loading) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div 
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm p-5 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Change Status</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Article:</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white break-words line-clamp-2">
            {articleTitle || 'Untitled Article'}
          </p>
        </div>
        
        <div className="space-y-2 mb-5">
          {statuses.map(status => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`w-full px-4 py-3 rounded-xl text-left flex items-center justify-between transition-colors ${
                (newStatus || currentStatus) === status
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500'
                  : 'bg-gray-50 dark:bg-gray-800 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="font-medium capitalize text-gray-900 dark:text-white text-sm">{status}</span>
              {(newStatus || currentStatus) === status && <Check className="h-5 w-5 text-emerald-500" />}
            </button>
          ))}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(newStatus)}
            disabled={loading || newStatus === currentStatus}
            className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Update
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Main Context Menu Component
const ContextMenu = ({ 
  position, 
  article, 
  onClose, 
  isVisible,
  onLikeClick,
  onDislikeClick,
  onCommentClick,
  onEditClick,
  onDeleteClick,
  onStatusChangeClick,
  onSaveClick
}) => {
  const menuRef = useRef(null);
  const { isAuthenticated, isAdmin, isContributor, user } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const canEdit = isAdmin || (isContributor && article?.author_id === user?.id);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDeleteModal || showStatusModal) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    const handleScroll = () => {
      if (!showDeleteModal && !showStatusModal) onClose();
    };
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showDeleteModal) setShowDeleteModal(false);
        else if (showStatusModal) setShowStatusModal(false);
        else onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose, showDeleteModal, showStatusModal]);

  useEffect(() => {
    if (!isVisible) {
      setShowDeleteModal(false);
      setShowStatusModal(false);
      setActionLoading(false);
    }
  }, [isVisible]);

  if (!isVisible || !article) return null;

  const adjustedPosition = { ...position };
  if (typeof window !== 'undefined') {
    const menuWidth = 220;
    const menuHeight = 400;
    if (position.x + menuWidth > window.innerWidth) {
      adjustedPosition.x = window.innerWidth - menuWidth - 10;
    }
    if (position.y + menuHeight > window.innerHeight) {
      adjustedPosition.y = window.innerHeight - menuHeight - 10;
    }
    if (adjustedPosition.y < 10) adjustedPosition.y = 10;
  }

  const handleOpenInNewTab = (e) => {
    e.stopPropagation();
    const link = article.url || article.link;
    if (link) window.open(link, '_blank');
    onClose();
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    const articleSlug = generateSlug(article.title);
    const link = article.url || article.link || `${window.location.origin}/news/${articleSlug}`;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => { setCopied(false); onClose(); }, 800);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    await onSaveClick?.(article);
    onClose();
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    await onLikeClick?.(article);
    onClose();
  };

  const handleDislike = async (e) => {
    e.stopPropagation();
    await onDislikeClick?.(article);
    onClose();
  };

  const handleComment = (e) => {
    e.stopPropagation();
    onCommentClick?.(article);
    onClose();
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEditClick?.(article);
    onClose();
  };

  const handleDeleteConfirm = async () => {
    setActionLoading(true);
    try {
      await onDeleteClick?.(article, isAdmin);
      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusConfirm = async (newStatus) => {
    setActionLoading(true);
    try {
      await onStatusChangeClick?.(article, newStatus, isAdmin);
      setShowStatusModal(false);
      onClose();
    } catch (error) {
      console.error('Status change failed:', error);
    } finally {
      setActionLoading(false);
    }
  };

  return createPortal(
    <>
      <div 
        ref={menuRef}
        style={{ position: 'fixed', left: adjustedPosition.x, top: adjustedPosition.y, zIndex: 99 }}
        className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 py-2 min-w-[200px] animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Public options */}
        <div className="pb-2 border-b border-gray-100 dark:border-gray-800">
          <MenuItem icon={ExternalLink} label="Open in New Tab" onClick={handleOpenInNewTab} />
          <MenuItem icon={copied ? Check : Copy} label={copied ? "Copied!" : "Copy Link"} onClick={handleCopyLink} />
        </div>

        {/* Save to Library - available for everyone */}
        <div className="py-2 border-b border-gray-100 dark:border-gray-800">
          <MenuItem icon={Bookmark} label="Save to Library" onClick={handleSave} />
        </div>

        {/* Logged-in user options - only for user-created articles */}
        {isAuthenticated && (article?.is_user_created || article?.is_localized) && (
          <div className="py-2 border-b border-gray-100 dark:border-gray-800">
            <MenuItem icon={Heart} label="Like" onClick={handleLike} />
            <MenuItem icon={MessageSquare} label="Comment" onClick={handleComment} />
          </div>
        )}

        {/* Admin/Author options */}
        {canEdit && (
          <div className="pt-2">
            <MenuItem icon={Edit2} label="Edit Article" onClick={handleEdit} />
            <MenuItem icon={RefreshCw} label="Change Status" onClick={(e) => { e.stopPropagation(); setShowStatusModal(true); }} />
            <MenuItem icon={Trash2} label={isAdmin ? "Delete Article" : "Request Deletion"} onClick={(e) => { e.stopPropagation(); setShowDeleteModal(true); }} danger />
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        articleTitle={article?.title}
        isAdmin={isAdmin}
        loading={actionLoading}
      />

      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleStatusConfirm}
        articleTitle={article?.title}
        currentStatus={article?.status || 'published'}
        isAdmin={isAdmin}
        loading={actionLoading}
      />
    </>,
    document.body
  );
};

// Provider Component
export const ArticleContextMenuProvider = ({ children }) => {
  const [menuState, setMenuState] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    article: null
  });
  
  const [editingArticle, setEditingArticle] = useState(null);
  const [commentArticle, setCommentArticle] = useState(null);
  const longPressTimerRef = useRef(null);
  const { isAuthenticated, isAdmin } = useAuth();
  const { showToast } = useToast();

  const showMenu = useCallback((article, x, y) => {
    setMenuState({ isVisible: true, position: { x, y }, article });
  }, []);

  const hideMenu = useCallback(() => {
    setMenuState(prev => ({ ...prev, isVisible: false }));
  }, []);

  // DISABLED: Context menu removed due to conflicts with iOS/Android native context menus
  const handleContextMenu = useCallback((e, article) => {
    // Do nothing - allow native context menu
  }, []);

  const handleTouchStart = useCallback((e, article) => {
    // Do nothing - disabled touch-and-hold menu
  }, []);

  const handleTouchEnd = useCallback(() => {
    // Do nothing
  }, []);

  // === ACTION HANDLERS ===
  
  const handleSave = useCallback(async (article) => {
    if (!article) return;
    
    if (isAuthenticated) {
      try {
        const token = localStorage.getItem('yegoNewsToken');
        const response = await axios.post(`${BACKEND_URL}/api/user/save-article`, 
          { article_id: article.id },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        // Also save locally for redundancy
        saveArticleLocally(article);
        window.dispatchEvent(new CustomEvent('articleSaved', {
          detail: { articleId: article.id, saved: response.data.saved }
        }));
        showToast(response.data.saved ? 'Saved to library!' : 'Removed from library', 'success');
      } catch (error) {
        console.error('Save failed:', error);
        // Fallback to local storage
        const saved = saveArticleLocally(article);
        showToast(saved ? 'Saved to library!' : 'Removed from library', 'success');
      }
    } else {
      // For unsigned users, save to localStorage
      const saved = saveArticleLocally(article);
      window.dispatchEvent(new CustomEvent('articleSaved', {
        detail: { articleId: article.id, saved }
      }));
      showToast(saved ? 'Saved to library!' : 'Removed from library', 'success');
    }
  }, [isAuthenticated, showToast]);

  const handleLike = useCallback(async (article) => {
    if (!isAuthenticated || !article) {
      showToast('Please sign in to like articles', 'warning');
      return;
    }
    
    // For external articles, just show a message - no interaction needed
    if (!article.is_user_created && !article.is_localized) {
      showToast('Likes are only available for YEGO articles', 'warning');
      return;
    }
    
    // For user-created articles, open the reader panel and like
    window.dispatchEvent(new CustomEvent('openArticleComments', { 
      detail: { article, scrollToComments: false } 
    }));
    
    // Perform the like
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.post(`${BACKEND_URL}/api/articles/${article.id}/like`, {}, 
        { headers: { Authorization: `Bearer ${token}` }}
      );
      window.dispatchEvent(new CustomEvent('articleReaction', {
        detail: { 
          articleId: article.id, 
          type: 'like',
          liked: response.data.liked,
          likeCount: response.data.like_count,
          dislikeCount: response.data.dislike_count
        }
      }));
      showToast(response.data.liked ? 'Liked!' : 'Like removed', 'like');
    } catch (error) {
      console.error('Like failed:', error);
      showToast('Failed to like article', 'error');
    }
  }, [isAuthenticated, showToast]);

  const handleDislike = useCallback(async (article) => {
    if (!isAuthenticated || !article) {
      showToast('Please sign in to dislike articles', 'warning');
      return;
    }
    
    // For external articles, just show a message - no interaction needed
    if (!article.is_user_created && !article.is_localized) {
      showToast('Reactions are only available for YEGO articles', 'warning');
      return;
    }
    
    // For user-created articles, open the reader panel
    window.dispatchEvent(new CustomEvent('openArticleComments', { 
      detail: { article, scrollToComments: false } 
    }));
    
    // Perform the dislike
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.post(`${BACKEND_URL}/api/articles/${article.id}/dislike`, {}, 
        { headers: { Authorization: `Bearer ${token}` }}
      );
      window.dispatchEvent(new CustomEvent('articleReaction', {
        detail: { 
          articleId: article.id, 
          type: 'dislike',
          disliked: response.data.disliked,
          dislikeCount: response.data.dislike_count,
          likeCount: response.data.like_count
        }
      }));
      showToast(response.data.disliked ? 'Disliked!' : 'Dislike removed', 'dislike');
    } catch (error) {
      console.error('Dislike failed:', error);
      showToast('Failed to dislike article', 'error');
    }
  }, [isAuthenticated, showToast]);

  const handleComment = useCallback((article) => {
    if (!article) return;
    
    // For external articles, just open in new tab - no comments
    if (!article.is_user_created && !article.is_localized) {
      if (article.link) {
        window.open(article.link, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    
    // For user-created articles, open the reader panel and scroll to comments
    window.dispatchEvent(new CustomEvent('openArticleComments', { 
      detail: { article, scrollToComments: true } 
    }));
  }, []);

  const handleEdit = useCallback((article) => {
    if (!article) return;
    // Set editing article to open modal on homepage
    setEditingArticle(article);
  }, []);

  const handleDelete = useCallback(async (article, isAdminUser) => {
    if (!article) return;
    try {
      const token = localStorage.getItem('yegoNewsToken');
      if (isAdminUser) {
        await axios.delete(`${BACKEND_URL}/api/admin/articles/${article.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.dispatchEvent(new CustomEvent('articleDeleted', { detail: { articleId: article.id } }));
        showToast('Article deleted successfully', 'success');
      } else {
        await axios.post(`${BACKEND_URL}/api/articles/${article.id}/deletion-request`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Deletion request sent to admin', 'success');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('Failed to delete article', 'error');
      throw error;
    }
  }, [showToast]);

  const handleStatusChange = useCallback(async (article, newStatus, isAdminUser) => {
    if (!article) return;
    try {
      const token = localStorage.getItem('yegoNewsToken');
      if (isAdminUser) {
        await axios.put(`${BACKEND_URL}/api/admin/articles/${article.id}/status`, 
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        window.dispatchEvent(new CustomEvent('articleStatusChanged', { 
          detail: { articleId: article.id, newStatus } 
        }));
        showToast(`Status changed to ${newStatus}`, 'success');
      } else {
        await axios.post(`${BACKEND_URL}/api/articles/${article.id}/status-request`, 
          { requested_status: newStatus },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        showToast('Status change request sent to admin', 'success');
      }
    } catch (error) {
      console.error('Status change failed:', error);
      showToast('Failed to change status', 'error');
      throw error;
    }
  }, [showToast]);

  // Close edit modal
  const closeEditModal = useCallback(() => {
    setEditingArticle(null);
  }, []);

  // Close comment panel
  const closeCommentPanel = useCallback(() => {
    setCommentArticle(null);
  }, []);

  const contextValue = {
    showMenu,
    hideMenu,
    handleContextMenu,
    handleTouchStart,
    handleTouchEnd,
    menuState,
    editingArticle,
    setEditingArticle,
    closeEditModal,
    commentArticle,
    closeCommentPanel,
    // Expose for direct use
    likeArticle: handleLike,
    dislikeArticle: handleDislike,
    saveArticle: handleSave,
    commentOnArticle: handleComment
  };

  return (
    <ArticleContextMenuContext.Provider value={contextValue}>
      {children}
      <ContextMenu
        position={menuState.position}
        article={menuState.article}
        onClose={hideMenu}
        isVisible={menuState.isVisible}
        onLikeClick={handleLike}
        onDislikeClick={handleDislike}
        onCommentClick={handleComment}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        onStatusChangeClick={handleStatusChange}
        onSaveClick={handleSave}
      />
    </ArticleContextMenuContext.Provider>
  );
};

export default ArticleContextMenuProvider;
